import axios from "axios";

// Baked in at build time (CRA reads `REACT_APP_*` from the shell or a `.env`
// file). Deployed builds never set it and get 8000, matching the deployed
// API's fixed port; a local build sets it to whatever host port the backend
// was actually published on, so remapping that port and remembering to also
// pass this are the same step, not two.
const LOCAL_API_PORT = process.env.REACT_APP_API_PORT || "8000";

export const API_BASE = window.location.hostname.includes("localhost")
  ? `http://localhost:${LOCAL_API_PORT}`
  : `https://api.${window.location.hostname.replace(/^www\./, "")}`;

const client = axios.create({ baseURL: API_BASE, timeout: 120000 });

/** FastAPI puts the readable message in `detail`; surface that, not "Request failed". */
function describe(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  if (error.response) return `${error.response.status} ${error.response.statusText}`;
  return `Cannot reach the server: ${error.message}`;
}

async function call(promise) {
  try {
    return (await promise).data;
  } catch (error) {
    throw new Error(describe(error));
  }
}

export const api = {
  /** What backend this is, including the version shown in the header. */
  getInfo: () => call(client.get("/api/info")),

  listRuns: () => call(client.get("/api/runs")),

  getRun: (runId) => call(client.get(`/api/runs/${runId}`)),

  createRun: ({ lcmsFiles, substanzenFile, createdBy, title, onProgress }) => {
    const form = new FormData();
    lcmsFiles.forEach((file) => form.append("lcms_files", file));
    form.append("substanzen_file", substanzenFile);
    if (createdBy) form.append("created_by", createdBy);
    if (title) form.append("title", title);
    return call(
      client.post("/api/runs", form, {
        onUploadProgress: (event) =>
          onProgress?.(event.total ? Math.round((100 * event.loaded) / event.total) : 0),
      })
    );
  },

  getQc: (runId) => call(client.get(`/api/runs/${runId}/qc`)),

  acknowledgeQc: (runId, acknowledgedBy) =>
    call(client.post(`/api/runs/${runId}/qc/acknowledge`, { acknowledged_by: acknowledgedBy })),

  getCompounds: (runId) => call(client.get(`/api/runs/${runId}/compounds`)),

  /**
   * `source` records how the decision was reached, not who made it:
   * `excluded_by` is the scientist whether they clicked the point or confirmed
   * a copilot proposal.
   */
  exclude: (runId, observationIds, reason, excludedBy, source = "direct") =>
    call(
      client.post(`/api/runs/${runId}/exclusions`, {
        observation_ids: observationIds,
        reason,
        excluded_by: excludedBy || null,
        source,
      })
    ),

  /**
   * Putting a point back is a curation decision too, so it is named the same
   * way an exclusion is. Query parameters because this is a DELETE; the server
   * copies the exclusion into the audit trail before removing it.
   */
  include: (runId, observationId, reinstatedBy, source = "direct") =>
    call(
      client.delete(`/api/runs/${runId}/exclusions/${encodeURIComponent(observationId)}`, {
        params: { reinstated_by: reinstatedBy || null, source },
      })
    ),

  /**
   * Is there a model to ask? Not run-scoped — what is configured belongs to
   * the installation. `kind` is "model", "stand-in" or "none"; the panel
   * disables itself on "none" and shows `reason` as-is.
   */
  getAssistantAvailability: () => call(client.get(`/api/assistant/availability`)),

  askAssistant: (runId, question, askedBy, conversationId) =>
    call(
      client.post(`/api/runs/${runId}/assistant/messages`, {
        question,
        asked_by: askedBy || null,
        conversation_id: conversationId || null,
      })
    ),

  /** The current chat, or a named one. Omitting the id gives the latest. */
  getAssistantTranscript: (runId, conversationId) =>
    call(
      client.get(`/api/runs/${runId}/assistant/messages`, {
        params: conversationId ? { conversation_id: conversationId } : undefined,
      })
    ),

  /**
   * Start a new chat. Mints a thread identifier and writes nothing: the
   * previous conversation stays in the transcript, it just stops being the
   * one the copilot is answering in.
   */
  startAssistantConversation: (runId) =>
    call(client.post(`/api/runs/${runId}/assistant/conversations`)),

  /**
   * The exploratory workspace. Nothing here reaches an approved result.
   *
   * Availability is true only when there is both a model to ask and a sandbox
   * to run programs in; either one missing greys the whole workspace out, so
   * the screen reads `reason` rather than working out which half is absent.
   */
  getExplorationAvailability: () => call(client.get(`/api/exploration/availability`)),

  /**
   * Open a session. The server pins the run's current state to it, and that
   * record never moves afterwards — curating the run later marks the session
   * stale rather than rewriting what its programs ran against.
   */
  startExploration: (runId, { title, createdBy } = {}) =>
    call(
      client.post(`/api/runs/${runId}/exploration/sessions`, {
        title: title || null,
        created_by: createdBy || null,
      })
    ),

  listExplorations: (runId) => call(client.get(`/api/runs/${runId}/exploration/sessions`)),

  getExploration: (runId, sessionId) =>
    call(client.get(`/api/runs/${runId}/exploration/sessions/${sessionId}`)),

  /**
   * Ask for an analysis. Slower than a copilot question by a different order
   * of magnitude: the agent writes a program and the program runs, so the
   * client's default timeout is not the right one here.
   */
  askExploration: (runId, sessionId, question, askedBy) =>
    call(
      client.post(
        `/api/runs/${runId}/exploration/sessions/${sessionId}/messages`,
        { question, asked_by: askedBy || null },
        { timeout: 300000 }
      )
    ),

  /** A figure or table a program produced. A plain URL, as the exports are. */
  explorationArtifactUrl: (runId, artifactId) =>
    `${API_BASE}/api/runs/${runId}/exploration/artifacts/${artifactId}`,

  /** Export URLs are plain links so the browser handles the download itself. */
  exportUrl: (runId, kind, query = "") =>
    `${API_BASE}/api/runs/${runId}/export/${kind}${query}`,

  /** An uploaded file back out of the archive, addressed by its checksum. */
  inputFileUrl: (runId, sha256) => `${API_BASE}/api/runs/${runId}/files/${sha256}`,
};
